import { useId, useMemo, useRef } from "react";
import type { PenStrokeCanvasObject } from "@refinement-canvas/domain";
import { getSharedStrokePath } from "../../../lib/pen-stroke";
import { PEN_HIT_MARGIN_SCREEN_PX } from "../../../lib/pen-stroke-hit";
import { resolveObjectCursor, useCanvasStore } from "../store/canvas-store";
import { useObjectDrag } from "../input/use-object-drag";
import { useCanvasOpDispatcherContext } from "../dispatcher-context";
import { PenStrokeSelectionOutline } from "./pen-stroke-selection-outline";
import {
  buildStrokeCenterlinePath,
  needsDottedStrokeStartFallback,
  resolveStrokePattern,
  strokeDashArray,
} from "../../../lib/stroke-pattern";

interface Props {
  readonly object: PenStrokeCanvasObject;
}

// 確定済みのペンストローク。通常オブジェクトと同じ zOrder で描画し、後から作ったものが上に重なる。
//
// 当たり判定はブラウザの SVG ヒットテストへ委ねる。ルートの <svg> は矩形ボックス全体で
// ヒットを取ってしまう（インライン SVG はレイアウト上ひとつの矩形を占める）ため常に
// pointerEvents: "none" とし、線の芯は塗りの <path>（pointer-events="painted"）が、
// 芯の周囲のマージンは判定専用の透明な <path>（pointer-events="stroke"）が受け持つ。
// <svg> 自体がヒットしなくても、子の <path> で発生したイベントは DOM ツリーを親へ
// バブルするため、useObjectDrag（<svg> へリスナを張る）と右クリックメニュー
// （canvas-objects-layer の ContextMenuTrigger）はそのまま機能する。
export function PenStrokeObject({ object }: Props): JSX.Element {
  const ref = useRef<SVGSVGElement>(null);
  // mask の id は url(#...) の一部になるため、外部入力される object.id から分離する。
  // useId は選択状態にかかわらず呼び、同じコンポーネントの再描画中は値を安定させる。
  const selectionMaskId = `pen-selection-mask-${useId()}`;
  const dispatcher = useCanvasOpDispatcherContext();
  const activeTool = useCanvasStore((s) => s.tool);
  // 単一選択時だけでなく、選択集合（Ctrl+クリックや範囲指定での複数選択を含む）に
  // 入っていればハイライトする。
  const isSelected = useCanvasStore((s) => s.selectedObjectIds.includes(object.id));
  const readOnly = useCanvasStore((s) => s.readOnly);
  // アウトラインは共有メモを通す。他参加者の選択輪郭が同じストロークを描くときに再算出しない。
  const path = useMemo(() => getSharedStrokePath(object), [object]);
  const pattern = resolveStrokePattern(object.strokePattern);
  const centerlinePath = useMemo(() => buildStrokeCenterlinePath(object.points), [object.points]);
  const dotted = pattern === "dotted";
  const dottedFallback = needsDottedStrokeStartFallback(
    object.points,
    object.thickness,
    pattern
  );

  useObjectDrag({
    objectId: object.id,
    elementRef: ref as unknown as React.RefObject<HTMLElement | null>,
    dispatcher,
    initialX: object.x,
    initialY: object.y,
    initialW: object.w,
    initialH: object.h,
    initialRotation: object.rotation,
    disabled: activeTool === "draw-pen",
  });

  // ペン描画中は既存ストロークもヒット対象から外し、上に重ねて描けるようにする（描画の途切れ防止）。
  const drawing = activeTool === "draw-pen";

  return (
    <svg
      ref={ref}
      data-testid={`canvas-object-${object.id}`}
      data-object-type="pen-stroke"
      // ブラウザのネイティブドラッグに pointer capture を奪われないようにする。
      {...{ draggable: false }}
      style={{
        position: "absolute",
        left: `${object.x}px`,
        top: `${object.y}px`,
        width: `${object.w}px`,
        height: `${object.h}px`,
        zIndex: object.zOrder,
        overflow: "visible",
        // ドラッグ開始時にテキスト選択が割り込まないようにする。
        userSelect: "none",
        // 外接矩形でヒットを取らない。当たり判定は子の <path> 側だけが持つ。
        pointerEvents: "none",
        // 配置ツール・ペン描画の選択中は新規作成（crosshair）であることを示し、移動と誤認させない。
        // カーソルの決定はここ（ルート）に残し、子の <path> は cursor: inherit で追従させる。
        // 子へ直接書くと、複製ドラッグ中に use-object-drag がルートへ設定する cursor: "copy" が
        // 反映されなくなる。
        cursor: resolveObjectCursor({ readOnly, activeTool }),
        transform: object.rotation ? `rotate(${object.rotation}deg)` : undefined,
        transformOrigin: "center",
      }}
      viewBox={`${object.x} ${object.y} ${object.w} ${object.h}`}
    >
      {/* 選択輪郭は最背面に置き、線そのものが輪郭線に隠れないようにする。 */}
      {isSelected ? (
        <PenStrokeSelectionOutline
          object={object}
          path={path}
          color="var(--color-selection)"
          maskId={selectionMaskId}
          testId="pen-stroke-selection-outline"
        />
      ) : null}
      {/* 判定専用。透明なので描画順は見た目に影響しない。stroke を明示するのは、
          pointer-events="stroke" の判定対象が「stroke の描画領域」であり、既定の
          stroke="none" ではヒット領域を持たない実装差を避けるため。 */}
      <path
        d={dotted ? centerlinePath : path}
        fill="none"
        stroke="transparent"
        strokeLinejoin="round"
        strokeLinecap="round"
        pointerEvents={drawing ? "none" : "stroke"}
        style={{
          strokeWidth: dotted
            ? `calc(${object.thickness}px + ${PEN_HIT_MARGIN_SCREEN_PX * 2}px * var(--canvas-inv-zoom, 1))`
            : `calc(${PEN_HIT_MARGIN_SCREEN_PX * 2}px * var(--canvas-inv-zoom, 1))`,
          cursor: "inherit",
        }}
      />
      {dotted ? (
        <>
          <path
            d={centerlinePath}
            fill="none"
            stroke={object.color}
            strokeWidth={object.thickness}
            strokeDasharray={strokeDashArray(object.thickness, pattern).join(" ")}
            strokeLinecap="round"
            strokeLinejoin="round"
            pointerEvents="none"
          />
          {dottedFallback && object.points[0] ? (
            <circle
              cx={object.points[0].x}
              cy={object.points[0].y}
              r={Math.max(0.01, object.thickness) / 2}
              fill={object.color}
              pointerEvents={drawing ? "none" : "painted"}
              style={{ cursor: "inherit" }}
            />
          ) : null}
        </>
      ) : (
        <path
          d={path}
          fill={object.color}
          pointerEvents={drawing ? "none" : "painted"}
          style={{ cursor: "inherit" }}
        />
      )}
    </svg>
  );
}
